// ReminderDaysPicker — multi-select chips for {0,1,3,7,14,30} (W3.1 §5.3 step 7).
//
// Single-select was the legacy pattern; the spec now requires multi-select.
// Selecting 「當天」(0) implicitly enables reminders; empty selection = off.
// When the resulting array is non-empty, an HH:MM input appears below.

import { useId } from "react";

import { FilterChipGroup } from "@/components/primitives/FilterChipGroup";
import {
  REMINDER_DAYS_ALLOWED,
  REMINDER_DAYS_LABEL_ZH,
  normalizeReminderDays,
  validateReminderDays,
} from "@/lib/anniversary/reminderDays";
import { cn } from "@/lib/utils/cn";

export interface ReminderDaysPickerProps {
  value: number[];
  /** HH:MM "20:00" or null (no reminder time). */
  reminderTime: string | null;
  onChange: (next: number[]) => void;
  onTimeChange: (next: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export function ReminderDaysPicker({
  value,
  reminderTime,
  onChange,
  onTimeChange,
  disabled = false,
  className,
}: ReminderDaysPickerProps) {
  const timeId = useId();
  const validation = validateReminderDays(value);
  const selected = normalizeReminderDays(value);

  // Multi-select: toggle a chip in/out of the list, then sort ascending.
  const toggle = (id: string) => {
    const n = Number(id);
    if (!REMINDER_DAYS_ALLOWED.includes(n)) return;
    const next = selected.includes(n)
      ? selected.filter((v) => v !== n)
      : [...selected, n];
    onChange(next);
    // Auto-clear reminder_time when reminders are disabled.
    if (next.length === 0) onTimeChange(null);
  };

  const items = REMINDER_DAYS_ALLOWED.map((d) => ({
    id: String(d),
    label: REMINDER_DAYS_LABEL_ZH[d] ?? `${d} 天`,
  }));

  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      data-component="ReminderDaysPicker"
      data-invalid={!validation.ok ? "true" : undefined}
    >
      <FilterChipGroup
        items={items}
        selectedId={null}
        onSelect={toggle}
        accent="you"
        ariaLabel="提醒天數 (多選)"
      />
      <div className="flex flex-wrap items-center gap-1.5 text-meta text-ink-2">
        {selected.length === 0 ? (
          <span data-testid="reminder-status">未啟用提醒</span>
        ) : (
          <span data-testid="reminder-status">
            已選 {selected.length} 個:{selected.map((v) => REMINDER_DAYS_LABEL_ZH[v]).join("、")}
          </span>
        )}
      </div>
      {selected.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={timeId} className="font-label text-ink-2">
            提醒時間 (HH:MM)
          </label>
          <input
            id={timeId}
            type="time"
            value={reminderTime ?? ""}
            onChange={(e) => onTimeChange(e.target.value || null)}
            disabled={disabled}
            data-testid="reminder-time"
            className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
        </div>
      ) : null}
      {!validation.ok && validation.message ? (
        <p className="text-meta text-partner-strong" data-testid="reminder-error">
          {validation.message}
        </p>
      ) : null}
    </div>
  );
}